import { AssessmentStepKeys, AssessmentType, Categories } from '@/enums'
import { paths } from '@/routes/paths'
import { useRequestRecommendation, useSaveAssessment } from '@/services'
import { useGoToPath, useLanguage } from '@/utils'
import { identity } from 'ramda'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AssessmentStepper } from '../AssessmentStepper'
import { NoOfferPage } from '../public/NoOfferPage'
import { useAssessmentSteps } from '../steps/hooks/useAssessmentSteps'
import { Loader } from '@/components'

interface AssessmentMessages {
  propsMessages: {
    helperText: string
    steps: string[]
  }
  subtitle: string
  subtitles: Record<string, string>
  titles: Record<string, string>
  assessmentSuccessButton: string
  assessmentSuccessSubtitle: string
  assessmentSuccessTitle: string
}

const useMessages = (): AssessmentMessages => {
  const { t } = useTranslation()

  return {
    propsMessages: {
      helperText: t('approxHelperText'),
      steps: [
        t('assessmentLoadingStep1'),
        t('liabilityAssessmentLoadingStep2'),
        t('liabilityAssessmentLoadingStep3'),
        t('assessmentLoadingStep5'),
      ],
    },
    subtitle: t('leadStepSubtitle'),
    subtitles: {
      [AssessmentStepKeys.businessTypeAi]: t(
        'selfAssessment.locationType.businessTypeSubtitleAi',
      ),
      [AssessmentStepKeys.businessTypeMainSelectionAi]: t(
        'selfAssessment.locationType.businessTypeMainSelectionAiSubtitle',
      ),
    },
    titles: {
      [AssessmentStepKeys.businessTypeAi]: t(
        'selfAssessment.locationType.businessTypeTitleAi',
      ),
      [AssessmentStepKeys.businessTypeConfirmationAi]: t(
        'selfAssessment.locationType.businessTypeConfirmationAiTitle',
      ),
      [AssessmentStepKeys.businessTypeMainSelectionAi]: t(
        'selfAssessment.locationType.businessTypeMainSelectionAiTitle',
      ),
      [AssessmentStepKeys.businessType]: t('businessTypeTitle'),
      [AssessmentStepKeys.companyDetails]: t('companyDetailsTitle'),
      [AssessmentStepKeys.companyFoundedDateLead]: t('companyFoundedDateTitle'),
      [AssessmentStepKeys.employeesCount]: t('employeesCountTitle'),
      [AssessmentStepKeys.approxRevenue]: t('approxRevenueTitle'),
      [AssessmentStepKeys.inventory]: t('inventoryTitle'),
      [AssessmentStepKeys.approxLaborCosts]: t('approxLaborCostsTitle'),
      [AssessmentStepKeys.prepareData]: t('preparingOffer'),
      [AssessmentStepKeys.prepareOfferB]: t('preparingOffer'),
      [AssessmentStepKeys.contactData]: t('createLeadTitle'),
      [AssessmentStepKeys.createAccount]: t('createAccountTitle'),
      [AssessmentStepKeys.createAccountB]: t('createAccountTitle'),
      [AssessmentStepKeys.startDate]: t('startDateTitle'),
      [AssessmentStepKeys.userProfile]: t('userProfileStepTitle'),
      [AssessmentStepKeys.locationDetails]: t('locationDetailsTitle'),
      [AssessmentStepKeys.costOfElectronics]: t('costOfElectronicsTitle'),
    },
    assessmentSuccessButton: t('assessmentSuccessButton'),
    assessmentSuccessSubtitle: t('assessmentSuccessSubtitle'),
    assessmentSuccessTitle: t('assessmentSuccessTitle'),
  }
}

const makeAdditionalSteps = (poaSigned: boolean): string[] => {
  return [
    AssessmentStepKeys.prepareOfferB,
    !poaSigned && AssessmentStepKeys.userProfile,
  ].filter(Boolean) as string[]
}

interface PartnerAssessmentProps {
  onLogoClick?: () => void
  categoryId: keyof typeof Categories
  locationId: string
  partnerSource: string
  poaSigned: boolean
  stepKeys: string[]
  makeStepProps: (propsMessages: {
    helperText: string
    steps: string[]
  }) => Record<string, any>
  stepperBreadcrumbTitles?: Record<string, string>
  initialValues?: Record<string, any>
  transformSubmitValues?: (values: any) => any
}

// eslint-disable-next-line import/no-unused-modules
export const PartnerAssessment = ({
  onLogoClick,
  categoryId,
  locationId,
  partnerSource,
  poaSigned,
  stepKeys,
  makeStepProps,
  stepperBreadcrumbTitles,
  initialValues = {},
  transformSubmitValues = identity,
}: PartnerAssessmentProps) => {
  const [errorState, setErrorState] = useState(false)
  const language = useLanguage()
  const messages = useMessages()
  const requestOfferMutation = useRequestRecommendation(locationId)
  const updateAssessmentMutation = useSaveAssessment(locationId)
  const goToRecommendation = useGoToPath(paths.viewRecommendationWithKey)
  const [showLoader, setShowLoader] = useState(false)
  const additionalSteps = useMemo(() => makeAdditionalSteps(poaSigned), [
    poaSigned,
  ])

  const aggregatedStepKeys = [...stepKeys, ...additionalSteps]

  const steps = useAssessmentSteps({
    categoryId,
    stepKeys: aggregatedStepKeys,
  })

  const stepProps = useMemo(
    () => ({
      ...makeStepProps(messages.propsMessages),
      [AssessmentStepKeys.businessTypeConfirmationAi]: {
        categoryId,
      },
      [AssessmentStepKeys.prepareData]: {
        stepsMessages: messages.propsMessages.steps,
        hideButtons: true,
      },
      [AssessmentStepKeys.prepareOfferB]: {
        hideButtons: true,
        categoryId,
        poaSigned,
        updateAssessmentMutation: updateAssessmentMutation,
        transformSubmitValues,
        isPartnerFunnel: true,
      },
      [AssessmentStepKeys.userProfile]: {
        isPartnerFunnel: true,
        updateAssessmentMutation: updateAssessmentMutation,
        isLoading:
          updateAssessmentMutation.isLoading || requestOfferMutation.isLoading,
        transformSubmitValues,
      },
    }),
    [language, categoryId, updateAssessmentMutation, requestOfferMutation],
  )

  const handleFinish = async (): Promise<void> => {
    setShowLoader(true)
    requestOfferMutation
      .mutateAsync({ categoryId, partnerSource })
      .then((data) => {
        if (data?.recommendationId) {
          goToRecommendation(data.recommendationId)
        } else {
          setErrorState(true)
        }
      })
      .finally(() => {
        setShowLoader(false)
      })
  }

  if (errorState) {
    return <NoOfferPage isPartner />
  }

  if (showLoader) {
    return <Loader />
  }

  return (
    <AssessmentStepper
      initialValues={initialValues}
      messages={messages}
      onFinish={handleFinish}
      onLogoClick={onLogoClick}
      skipSuccessPage
      stepKeys={steps}
      stepperBreadcrumbTitles={stepperBreadcrumbTitles}
      stepProps={stepProps}
      type={AssessmentType.firstTimeUser}
    />
  )
}
